import PageTitle from "../../layouts/PageTitle";
import { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPartnersFunc } from "../../../store/custom-actions/partners-actions/GetPartnersSlicer";
import { deletePartnerFunc } from "../../../store/custom-actions/partners-actions/DeletePartnerSlicer";
import LoadingScreen from "../../components/loading-screen/LoadingScreen";
import { useTranslation } from "react-i18next";
import Pagination from "../../components/ui/Pagination";
import Table from "../../components/ui/Table";
import SearchBox from "../../components/ui/SearchBox";
import { handleDeleteEntity } from "../../../utils/deleteHelper";
import NewPartnerForm from "../../custom-forms/partners/NewPartnerForm";
import UpdatePartnerForm from "../../custom-forms/partners/UpdatePartnerForm";
import getImageUrl from "../../../utils/getImageUrl";

function PartnersPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [showNewPartnerForm, setShowNewPartnerForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [partnerToUpdate, setPartnerToUpdate] = useState({});
  
  const { loading: getPartnersLoading, data: partnersData } = useSelector(
    (state) => state.getPartnersSlicer
  );
  const { loading: deletePartnerLoading } = useSelector(
    (state) => state.deletePartnerSlicer
  );

  useEffect(() => {
    dispatch(getPartnersFunc());
  }, [dispatch]);

  // Get current page data directly from partnersData
  const getCurrentPageData = () => {
    if (!partnersData || partnersData.length === 0) return [];
    return partnersData.slice(
      activePag.current * sort,
      (activePag.current + 1) * sort
    );
  };

  const filterFunc = () => {
    let api = "?";
    if (searchValue !== "") {
      api += `&search=${searchValue}`;
    }
    if (isActiveFilter !== "") {
      api += `&is_active=${isActiveFilter}`;
    }
    // Reset pagination when filtering
    activePag.current = 0;
    dispatch(getPartnersFunc(api));
  };

  const sort = 20;
  const activePag = useRef(0);

  const onClick = (i) => {
    activePag.current = i;
  };

  const handleDeleteItem = (id, entityName = "") => {
    handleDeleteEntity({
      id,
      entityName: entityName,
      deleteFunc: deletePartnerFunc,
      dispatch,
      refetchFunc: () => {
        // Reset pagination and refresh data
        activePag.current = 0;
        dispatch(getPartnersFunc());
      },
      t,
    });
  };

  return (
    <>
      <PageTitle
        motherMenu={"Dashboard"}
        activeMenu={"Partners"}
        customClasses={"my-0"}
      />

      <div className="col-12">
        <div className="card">
          <div className="card-header row">
            <div className="col-6">
              <SearchBox
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                onSearch={() => filterFunc()}
              />
            </div>
            <div className="col-6 text-end">
              <select
                className="form-select me-2"
                value={isActiveFilter}
                onChange={(e) => {
                  setIsActiveFilter(e.target.value);
                  setTimeout(() => filterFunc(), 100);
                }}
                style={{ width: "auto", display: "inline-block" }}
              >
                <option value="">{t("All Status")}</option>
                <option value="true">{t("Active")}</option>
                <option value="false">{t("Inactive")}</option>
              </select>
              <button
                className="btn btn-primary btn-md"
                onClick={() => setShowNewPartnerForm(true)}
              >
                {t("Create")}
              </button>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive w-100">
              <div id="example_wrapper" className="dataTables_wrapper">
                <Table
                  showIndex={true}
                  startIndex={activePag.current * sort}
                  columns={[
                    { 
                      header: t("Name"), 
                      render: (row) => row.name 
                    },
                    {
                      header: t("Logo"),
                      render: (row) => (
                        row.logo ? (
                          <img 
                            src={getImageUrl(row.logo.path)} 
                            alt={row.name}
                            style={{ width: "50px", height: "50px", objectFit: "contain" }}
                            className="rounded"
                          />
                        ) : (
                          <span className="text-muted">{t("No Logo")}</span>
                        )
                      ),
                    },
                    {
                      header: t("Sort Order"),
                      render: (row) => row.sort_order || 0,
                    },
                    {
                      header: t("Status"),
                      render: (row) => (
                        <span
                          className={`badge ${
                            row.is_active ? "bg-success" : "bg-danger"
                          }`}
                        >
                          {row.is_active ? t("Active") : t("Inactive")}
                        </span>
                      ),
                    },
                    {
                      header: t("Created At"),
                      render: (row) => new Date(row.created_at).toLocaleDateString(),
                    },
                  ]}
                  data={getCurrentPageData()}
                  loading={getPartnersLoading}
                  actions={(partner) => (
                    <>
                      <button
                        className="btn btn-primary shadow btn-xs sharp me-1"
                        onClick={() => {
                          setShowUpdateForm(true);
                          setPartnerToUpdate(partner);
                        }}
                      >
                        <i className="fas fa-pencil-alt"></i>
                      </button>
                      <button
                        className="btn btn-danger shadow btn-xs sharp"
                        onClick={() => handleDeleteItem(partner.id, partner.name)}
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </>
                  )}
                />

                <Pagination
                  totalItems={partnersData?.length || 0}
                  currentPage={activePag.current}
                  perPage={sort}
                  onPageChange={(page) => onClick(page)}
                  t={t}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <LoadingScreen loading={deletePartnerLoading} />
      
      <NewPartnerForm
        show={showNewPartnerForm}
        onHide={() => {
          setShowNewPartnerForm(false);
          dispatch(getPartnersFunc());
        }}
      />
      
      <UpdatePartnerForm
        show={showUpdateForm}
        onHide={() => {
          setShowUpdateForm(false);
          dispatch(getPartnersFunc());
        }}
        partnerId={partnerToUpdate.id}
        oldName={partnerToUpdate.name}
        oldLogoId={partnerToUpdate.logo_id}
        oldSortOrder={partnerToUpdate.sort_order}
        oldStatus={partnerToUpdate.is_active}
        oldLogo={partnerToUpdate.logo}
      />
    </>
  );
}

export default PartnersPage;
